import { useCallback, useRef, useState } from "react";
import { ClientPropertyTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import type { BuildingFormInput } from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import type { FlatFormInput } from "web_myplann/Portfolio/Forms/FlatForm/flatFormSchema";
import type { LandFormInput } from "web_myplann/Portfolio/Forms/LandForm/landFormSchema";
import {
	DEFAULT_REAL_ESTATE_TYPE,
	type RealEstateType,
} from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";

type RealEstateDrafts = {
	[ClientPropertyTypeEnum.Flat]?: FlatFormInput;
	[ClientPropertyTypeEnum.House]?: BuildingFormInput;
	[ClientPropertyTypeEnum.Cottage]?: BuildingFormInput;
	[ClientPropertyTypeEnum.Land]?: LandFormInput;
};

type RealEstateDraft = NonNullable<RealEstateDrafts[RealEstateType]>;

export const useBuildingDraftForm = () => {
	const [type, setType] = useState<RealEstateType>(DEFAULT_REAL_ESTATE_TYPE);

	const drafts = useRef<RealEstateDrafts>({});
	const snapshotRef = useRef<(() => RealEstateDraft) | null>(null);

	const registerSnapshot = useCallback((getValues: () => RealEstateDraft) => {
		snapshotRef.current = getValues;
	}, []);

	const getDraft = useCallback(
		<T extends RealEstateType>(realEstateType: T): RealEstateDrafts[T] =>
			drafts.current[realEstateType],
		[],
	);

	const storeDraft = <T extends RealEstateType>(
		realEstateType: T,
		values: RealEstateDrafts[T],
	) => {
		drafts.current[realEstateType] = values;
	};

	const onTypeChange = (nextType: RealEstateType) => {
		if (snapshotRef.current) {
			storeDraft(type, snapshotRef.current());
		}

		snapshotRef.current = null;
		setType(nextType);
	};

	return { type, onTypeChange, registerSnapshot, getDraft };
};
